import type { FC } from 'react'

import { Input, LoaderCircle, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { COUNTRY_FLAGS, countryOptions, getDefaultCountryId } from '@skytech/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, FormikProvider, useFormik, Form } from 'formik'
import * as yup from 'yup'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useProfile } from '@/hooks/user'

import type { CustomNumberStateType, NumberRecordType, PhoneBookType } from '../types'

import queries from '../../queries'
import adminResources from '../../resources'

type Props = {
  onClose: () => void
  selectedPhoneBook: PhoneBookType
  initialData: CustomNumberStateType | undefined
}

export const CustomPhoneNumberForm: FC<Props> = ({ onClose, selectedPhoneBook, initialData }) => {
  const user = useProfile()
  const { locale } = useAppContext()
  const queryClient = useQueryClient()
  const translate = useTranslation()

  const translatedMandatoryMessage = translate('This field is mandatory')
  const translatedLengthMessage = (len: number) =>
    translate(`The length of the value must be less than ${len} characters`)

  const validationSchema = yup.object({
    number: yup
      .string()
      .max(100, obj => translatedLengthMessage(obj.max))
      .required(translatedMandatoryMessage),
    name: yup
      .string()
      .max(100, obj => translatedLengthMessage(obj.max))
      .required(translatedMandatoryMessage),
  })

  const form = useFormik({
    initialValues: {
      number: initialData?.number || '',
      name: initialData?.name || '',
      country: initialData?.countryId || user?.countryId || getDefaultCountryId(locale),
    },
    validationSchema: validationSchema,
    onSubmit: formValues => {
      const data = {
        number: formValues.number,
        name: formValues.name,
        countryId: formValues.country,
      }

      if (initialData?.id) {
        onUpdateNumbers(data)
      } else {
        onAddNumbers(data)
      }
    },
  })

  const addToGroup = useMutation(adminResources.addNumbersToPhoneGroup, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminActiveNumbersList())
      form.resetForm()
      onClose()
    },
  })

  const updatePhoneGroupNumber = useMutation(adminResources.updatePhoneGroupNumber, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminActiveNumbersList())
      form.resetForm()
      onClose()
    },
  })

  const addToPhoneBook = useMutation(adminResources.addNumbersToPhoneBook, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminActiveNumbersList())
      form.resetForm()
      onClose()
    },
  })

  const updatePhoneBookNumber = useMutation(adminResources.updatePhoneBookNumber, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminActiveNumbersList())
      form.resetForm()
      onClose()
    },
  })

  const onAddNumbers = (data: Omit<NumberRecordType, 'id'>) => {
    if (selectedPhoneBook) {
      const payload = {
        groupId: Number(selectedPhoneBook.id),
        numbers: [data],
      }

      if (selectedPhoneBook.type === 'phone-book') {
        addToPhoneBook.mutate(payload)
      } else {
        addToGroup.mutate(payload)
      }
    }
  }

  const onUpdateNumbers = (data: Omit<NumberRecordType, 'id'>) => {
    if (selectedPhoneBook) {
      const payload = {
        groupId: Number(selectedPhoneBook.id),
        numberId: Number(initialData?.id),
        data,
      }

      if (selectedPhoneBook.type === 'phone-book') {
        updatePhoneBookNumber.mutate(payload)
      } else {
        updatePhoneGroupNumber.mutate(payload)
      }
    }
  }

  const isLoading =
    addToGroup.isLoading ||
    addToPhoneBook.isLoading ||
    updatePhoneGroupNumber.isLoading ||
    updatePhoneBookNumber.isLoading

  return (
    <>
      {isLoading && (
        <LoaderCircle
          size="regular"
          className="absolute z-50 flex h-full w-full translate-x-[-1.70rem] translate-y-[-1.25rem] items-center justify-center rounded-[1rem] bg-[#0f0f0f33]"
        />
      )}
      <h2 className="mb-0">
        <Translation id="Add custom numbers" />
      </h2>
      <FormikProvider value={form}>
        <Form>
          <div className="flex flex-col p-4">
            <div className="mb-5">
              <div className="z-50 mb-2 flex w-full gap-2">
                <Field name="country">
                  {({ field, form }: FieldProps) => (
                    <Select
                      id="country"
                      options={countryOptions.map(option => ({
                        value: option.value,
                        label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                      }))}
                      {...field}
                      className="w-2/5"
                      onChange={option => {
                        form.setFieldValue(field.name, option?.value)
                      }}
                      label={<Translation id="Phone number" />}
                    />
                  )}
                </Field>

                <Field name="number">
                  {({ field, meta }: FieldProps) => (
                    <Input
                      isFullWidth
                      id="number"
                      ariaLabel={translate('Phone number')}
                      touched={meta.touched}
                      error={meta.error}
                      placeholder={translate('Enter phone number')}
                      {...field}
                      label="&nbsp;"
                    />
                  )}
                </Field>
              </div>

              <Field name="name">
                {({ field, meta }: FieldProps) => (
                  <div className="w-full">
                    <Input
                      isFullWidth
                      className="pl-2"
                      id="name"
                      placeholder={translate('Enter name')}
                      error={meta.error}
                      touched={meta.touched}
                      {...field}
                      label={<Translation id="Name:" />}
                    />
                  </div>
                )}
              </Field>
            </div>
            <div className="gap flex w-full justify-end overflow-hidden">
              <Button className="w-20" disabled={isLoading} onClick={onClose}>
                <Translation id="Cancel" />
              </Button>

              <Button variant="primary" type="submit" className="w-20" isLoading={isLoading}>
                <Translation id={!initialData?.id ? 'Add' : 'Edit'} />
              </Button>
            </div>
          </div>
        </Form>
      </FormikProvider>
    </>
  )
}
